/**
 * Phase 7.3 — Schema-driven re-edit UI.
 *
 * Each entry: { key, label, type, required?, options?, min?, max?, rows? }
 *
 * type ∈ text | textarea | number | date | time | select | checkbox
 *         | rows(items|services|guests|bills) | readonly
 *
 * For dropdowns whose options depend on runtime data (main dept, HOD list),
 * we set `type: "select"` and `optionsFrom: "mainDept"|"subDept"|"hods"` so
 * the modal wires them from props.
 *
 * Keys mirror the payload shape POSTed by every `New*.jsx` form. Editing
 * writes back the same keys, so backward-compat with old requisitions is
 * automatic.
 */

const REQUESTED_BY = [
  { key: "staffName", label: "Applicant Name", type: "text", required: true },
  { key: "staffEmail", label: "Applicant Email", type: "text", required: true },
  { key: "staffContact", label: "Applicant Contact", type: "text" },
  { key: "mainDept", label: "Main Department", type: "select", optionsFrom: "mainDept", required: true },
  { key: "subDept", label: "Sub Department", type: "select", optionsFrom: "subDept", required: true },
  { key: "hodApproval", label: "HOD (for consent & approval)", type: "select", optionsFrom: "hods", required: true },
];

const EMERGENCY = [
  { key: "is_emergency", label: "Emergency", type: "checkbox" },
  { key: "emergency_reason", label: "Emergency Reason (≥10 chars if emergency)", type: "textarea" },
];

const MATERIAL_ITEM_ROW = [
  { key: "name", label: "Name", type: "text", required: true },
  { key: "quantity", label: "Qty", type: "text", required: true },
  { key: "cost", label: "Approx Cost", type: "text" },
  { key: "remarks", label: "Remarks", type: "text" },
];

const SERVICE_ITEM_ROW = MATERIAL_ITEM_ROW;

const BILL_ROW = [
  { key: "sr_no", label: "S.No", type: "text", required: true },
  { key: "bill_type", label: "Bill Type", type: "text", required: true },
];

const GUEST_ROW = [
  { key: "name", label: "Guest Name", type: "text", required: true },
  { key: "relation", label: "Relation", type: "text" },
  { key: "idType", label: "ID Type", type: "text" },
  { key: "idNumber", label: "ID Number", type: "text" },
];

export const RE_EDIT_SCHEMAS = {
  material: [
    ...REQUESTED_BY, ...EMERGENCY,
    { key: "materialRequiredBy", label: "Material Required For", type: "text" },
    { key: "materialCategory", label: "Material Category", type: "text" },
    { key: "purposeOfUtility", label: "Purpose of Utility", type: "textarea", required: true },
    { key: "requiredByDate", label: "Required By", type: "date", required: true },
    { key: "items", label: "Materials", type: "rows", rows: MATERIAL_ITEM_ROW },
  ],
  service: [
    ...REQUESTED_BY, ...EMERGENCY,
    { key: "materialRequiredBy", label: "Service Required For", type: "text" },
    { key: "materialCategory", label: "Service Category", type: "text" },
    { key: "purposeOfUtility", label: "Purpose of Utility", type: "textarea", required: true },
    { key: "requiredByDate", label: "Required By", type: "date", required: true },
    { key: "services", label: "Services", type: "rows", rows: SERVICE_ITEM_ROW },
  ],
  event: [
    ...REQUESTED_BY, ...EMERGENCY,
    { key: "eventName", label: "Event Name", type: "text", required: true },
    { key: "eventDate", label: "Event Date", type: "date", required: true },
    { key: "startTime", label: "Start Time", type: "time" },
    { key: "endTime", label: "End Time", type: "time" },
    { key: "eventPurpose", label: "Event Purpose", type: "textarea" },
    { key: "expectedAttendance", label: "Expected Attendance", type: "number" },
  ],
  transportation: [
    ...REQUESTED_BY, ...EMERGENCY,
    { key: "purposeOfTravel", label: "Purpose of Travel", type: "textarea", required: true },
    { key: "tripType", label: "Trip Type", type: "select",
      options: ["One Way", "Round Trip", "Multi-stop"] },
    { key: "modeOfTransport", label: "Mode of Transport", type: "text" },
    { key: "peopleCount", label: "People Count", type: "number" },
    { key: "travelDate", label: "Travel Date", type: "date" },
    { key: "reportingTime", label: "Reporting Time", type: "time" },
    { key: "departureLocation", label: "Departure Location", type: "text" },
    { key: "destinationLocation", label: "Destination Location", type: "text" },
    { key: "payment_source", label: "Payment Source", type: "select",
      options: ["School", "Personal", "Vendor"] },
  ],
  fnb: [
    ...REQUESTED_BY, ...EMERGENCY,
    { key: "purposeOfRequirement", label: "Purpose", type: "textarea" },
    { key: "peopleCount", label: "People Count", type: "number" },
    { key: "requirementDate", label: "Date", type: "date" },
    { key: "requirementTime", label: "Time", type: "time" },
    { key: "location", label: "Location", type: "text" },
  ],
  // Phase 13 — Cake schema (returned by getReeditSchema when
  // form_type=fnb AND requirement_type=cake).
  _fnb_cake: [
    ...REQUESTED_BY, ...EMERGENCY,
    { key: "cake_to_whom", label: "Cake To Whom", type: "select",
      options: ["Student", "Staff", "Guest"], required: true },
    { key: "cake_student_name", label: "Student Name (if student)", type: "text" },
    { key: "cake_student_grade", label: "Grade (if student)", type: "text" },
    { key: "cake_student_gr_number", label: "GR Number (if student)", type: "text" },
    { key: "cake_student_day_boarding", label: "Day / Boarding (if student)", type: "select",
      options: ["", "Day School", "Boarding"] },
    { key: "cake_weight", label: "Cake Weight", type: "select",
      options: ["0.5 kg", "1 kg", "1.5 kg", "2 kg", "3 kg", "5 kg"], required: true },
    { key: "cake_flavor", label: "Cake Flavor", type: "select",
      options: ["Chocolate", "Vanilla", "Strawberry", "Butterscotch",
                "Black Forest", "Red Velvet", "Pineapple", "Other"], required: true },
    { key: "cake_message", label: "Message on Cake", type: "text" },
    { key: "cutleries_required_cake", label: "Cutleries Required", type: "select",
      options: ["Yes", "No"] },
    { key: "cake_required_date", label: "Required Date", type: "date", required: true },
    { key: "cake_required_time", label: "Required Time", type: "time", required: true },
    { key: "cake_required_location", label: "Required Location", type: "text", required: true },
  ],
  travel: [
    ...REQUESTED_BY, ...EMERGENCY,
    { key: "purposeOfTravel", label: "Purpose of Travel", type: "textarea" },
    { key: "peopleCount", label: "People Count", type: "number" },
    { key: "travelVehicle", label: "Vehicle", type: "text" },
    { key: "country", label: "Country", type: "text" },
    { key: "state", label: "State", type: "text" },
    { key: "city", label: "City", type: "text" },
    { key: "departureDate", label: "Departure", type: "date" },
    { key: "arrivalDate", label: "Arrival", type: "date" },
  ],
  hotel: [
    ...REQUESTED_BY, ...EMERGENCY,
    { key: "purposeOfBooking", label: "Purpose of Booking", type: "textarea" },
    { key: "peopleCount", label: "People Count", type: "number" },
    { key: "daysOfStay", label: "Days of Stay", type: "number" },
    { key: "checkInDate", label: "Check-in", type: "date" },
    { key: "checkOutDate", label: "Check-out", type: "date" },
    { key: "country", label: "Country", type: "text" },
    { key: "state", label: "State", type: "text" },
    { key: "city", label: "City", type: "text" },
    { key: "hotelName", label: "Hotel Name", type: "text" },
    { key: "hotelAddress", label: "Hotel Address", type: "textarea" },
  ],
  guest: [
    { key: "employeeName", label: "Employee Name", type: "text" },
    { key: "designation", label: "Designation", type: "text" },
    { key: "mainDept", label: "Main Department", type: "select", optionsFrom: "mainDept" },
    { key: "subDept", label: "Sub Department", type: "select", optionsFrom: "subDept" },
    { key: "flatNo", label: "Flat No", type: "text" },
    { key: "maritalStatus", label: "Marital Status", type: "select",
      options: ["Single", "Married"] },
    { key: "mobileNo", label: "Mobile", type: "text" },
    { key: "vehicleType", label: "Vehicle", type: "select",
      options: ["Not Applicable", "Two-Wheeler", "Four-Wheeler"] },
    { key: "vehicleRegNo", label: "Vehicle Reg No", type: "text" },
    { key: "daysCount", label: "Days", type: "number" },
    { key: "fromDate", label: "From", type: "date" },
    { key: "toDate", label: "To", type: "date" },
    { key: "guests", label: "Guests", type: "rows", rows: GUEST_ROW },
  ],
  advance_requisition: [
    ...REQUESTED_BY, ...EMERGENCY,
    { key: "advanceAmount", label: "Advance Required (₹)", type: "number", required: true },
    { key: "toShri", label: "To Shri /Smt.", type: "text" },
    { key: "purpose", label: "Purpose", type: "textarea", required: true },
    { key: "previousAdvanceBalance", label: "Previous Advance Bal.", type: "number" },
  ],
  pocket_money: [
    ...REQUESTED_BY, ...EMERGENCY,
    { key: "studentName", label: "Student Name", type: "text", required: true },
    { key: "grade", label: "Grade", type: "text", required: true },
    { key: "amount", label: "Amount (₹)", type: "number", required: true },
    { key: "purpose", label: "Purpose", type: "textarea", required: true },
    { key: "encl", label: "Encl:", type: "text" },
  ],
  advance_settlement: [
    ...REQUESTED_BY, ...EMERGENCY,
    { key: "personName", label: "Name of Person", type: "text", required: true },
    { key: "designation", label: "Designation", type: "text", required: true },
    { key: "advanceDrawn", label: "Advance Drawn (₹)", type: "number", required: true },
    { key: "totalExpenses", label: "Total Expenses", type: "number" },
    { key: "balanceRefunded", label: "Balance Refunded", type: "number" },
    { key: "excessOverAdvance", label: "Excess Over Advance", type: "number" },
    { key: "bills", label: "Bills", type: "rows", rows: BILL_ROW },
  ],
  reimbursement: [
    ...REQUESTED_BY, ...EMERGENCY,
    { key: "employeeName", label: "Employee Name", type: "text", required: true },
    { key: "empCode", label: "Emp. Code", type: "text", required: true },
    { key: "travelApprovedBy", label: "Travel Approved By", type: "text" },
    { key: "purposeOfTravel", label: "Purpose of Travel", type: "textarea", required: true },
    { key: "daFromDate", label: "From Date", type: "date" },
    { key: "daToDate", label: "To Date", type: "date" },
    { key: "daNoOfDays", label: "No. of Days", type: "number" },
    { key: "daAmountCalculated", label: "Amount Calculated", type: "number" },
    { key: "bills", label: "Bills", type: "rows", rows: BILL_ROW },
  ],
};

/**
 * Phase 13 — schema selector that branches fnb by requirement_type.
 * Prefer this over direct `RE_EDIT_SCHEMAS[form_type]` lookups.
 */
export function getReeditSchema(form_type, form_data) {
  if (form_type === "fnb"
      && (form_data?.requirement_type || "").toLowerCase() === "cake") {
    return RE_EDIT_SCHEMAS._fnb_cake;
  }
  return RE_EDIT_SCHEMAS[form_type] || [];
}
